import { PopoverButton } from "@headlessui/react";
import { CalendarIcon, XMarkIcon } from "@heroicons/react/24/outline";
import React from "react";

export type SessionDatePopoverTriggerVariant = "reportBuilder" | "freeform";

export interface SessionDatePopoverTriggerProps {
  variant: SessionDatePopoverTriggerVariant;
  displayText: string;
  disabled?: boolean;
  hasSelection: boolean;
  onClear: (e: React.MouseEvent) => void;
  "data-testid"?: string;
}

export const SessionDatePopoverTrigger: React.FC<
  SessionDatePopoverTriggerProps
> = ({
  variant,
  displayText,
  disabled = false,
  hasSelection,
  onClear,
  "data-testid": dataTestId,
}) => {
  const isReportBuilder = variant === "reportBuilder";

  const buttonClassName = isReportBuilder
    ? `bg-[#252527] relative rounded-[6px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] w-full focus:outline-none focus:ring-0 border border-white/10 hover:border-orange-500 transition-all ${
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
      }`
    : `bg-[#0b0b0b] relative rounded-[8px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] w-full focus:outline-none focus:ring-0 border border-[#2a2a2a] border-solid ${
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
      }`;

  const buttonStyle: React.CSSProperties = isReportBuilder
    ? { height: "38px", minHeight: "38px" }
    : {
        fontFamily: "Inter, sans-serif",
        height: "36px",
        minHeight: "36px",
        borderWidth: "0.833px",
      };

  const innerRowClass = isReportBuilder
    ? "box-border content-stretch flex gap-1 items-center px-3 relative w-full h-full"
    : "box-border content-stretch flex gap-1 items-center px-[13px] py-[9px] relative w-full h-full";

  const labelClass = isReportBuilder
    ? `basis-0 font-medium grow leading-6 min-h-px min-w-px not-italic relative shrink-0 text-sm text-left ${
        hasSelection ? "text-white" : "text-[#9ca3af]"
      }`
    : `basis-0 font-medium grow leading-6 min-h-px min-w-px not-italic relative shrink-0 text-base text-left ${
        hasSelection ? "text-white" : "text-[#B4AFAF]"
      }`;

  const clearHoverClass = isReportBuilder
    ? "shrink-0 p-0.5 rounded hover:bg-[#3a3a3c] transition-colors focus:outline-none focus:ring-0 cursor-pointer"
    : "shrink-0 p-0.5 rounded hover:bg-[#2a2a2a] transition-colors focus:outline-none focus:ring-0 cursor-pointer";

  return (
    <PopoverButton
      type="button"
      disabled={disabled}
      data-testid={dataTestId}
      className={buttonClassName}
      style={buttonStyle}
    >
      <div className="flex flex-row items-center overflow-clip rounded-[inherit] h-full w-full">
        <div className={innerRowClass}>
          <p className={labelClass}>{displayText}</p>
          {hasSelection && (
            <div
              onClick={onClear}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClear(e as unknown as React.MouseEvent);
                }
              }}
              className={clearHoverClass}
              role="button"
              tabIndex={0}
              aria-label="Clear selected dates"
            >
              <XMarkIcon className="w-4 h-4 text-[#B4AFAF] stroke-2" />
            </div>
          )}
          <CalendarIcon
            className={
              isReportBuilder
                ? "w-5 h-5 text-white shrink-0"
                : "w-5 h-5 text-white shrink-0 stroke-2"
            }
          />
        </div>
      </div>
    </PopoverButton>
  );
};
